import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useJsApiLoader, GoogleMap } from '@react-google-maps/api';
import styled, { useTheme } from 'styled-components';
import { GOOGLE_MAPS_LIBRARIES } from '../../services/googleMapsConfig';
const MAP_STYLE = { width: '100%', height: '200px', borderRadius: '6px', marginTop: '4px' };

// ─── Styled ────────────────────────────────────────────────────────────────────

const SearchWrap = styled.div``;

const InputWrap = styled.div`position: relative;`;

const SearchRow = styled.div`display: flex; gap: 6px; align-items: center;`;

const SearchInp = styled.input`
  flex: 1; height: 36px; padding: 0 10px; box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px; background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.txtPrincipal}; font-size: 0.9rem;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.info}33`}; }
  &::placeholder { color: ${({ theme }) => theme.colors.txtSecundario}; }
`;

const ClearBtn = styled.button`
  height: 36px; padding: 0 10px; flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px; background: none;
  color: ${({ theme }) => theme.colors.txtSecundario};
  font-size: 0.8rem; cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.danger}; color: ${({ theme }) => theme.colors.danger}; }
`;

const SuggestionsList = styled.ul`
  position: absolute; top: 100%; left: 0; right: 0; z-index: 100;
  list-style: none; margin: 0; padding: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-top: none; border-radius: 0 0 6px 6px;
  box-shadow: 0 4px 16px ${({ theme }) => `${theme.colors.sombra}40`};
  max-height: 220px; overflow-y: auto;
`;

const SuggestionItem = styled.li`
  display: flex; align-items: baseline; gap: 6px;
  padding: 8px 10px; font-size: 13px; cursor: pointer;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  &:last-child { border-bottom: none; }
  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; }
`;

const SuggMain = styled.span`flex-shrink: 0; max-width: 55%; font-size: 13px;`;

const SuggSec = styled.span`
  font-size: 11px; color: ${({ theme }) => theme.colors.txtSecundario};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;
`;

const SelectedAddr = styled.div`
  font-size: 0.78rem; color: ${({ theme }) => theme.colors.txtPrincipal};
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px; padding: 6px 10px; margin-top: 6px;
  display: flex; align-items: center; gap: 6px;
`;

const MapHint = styled.div`
  font-size: 0.72rem; color: ${({ theme }) => theme.colors.txtSecundario};
  padding: 4px 0 2px;
`;

const NoKeyMsg = styled.div`
  font-size: 0.78rem; color: ${({ theme }) => theme.colors.txtSecundario};
  border: 1px dashed ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px; padding: 12px; text-align: center;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const KEY_VALID = API_KEY && API_KEY !== 'TU_CLAVE_AQUI';

function parseAddressComponents(comps) {
  const get = (...types) => {
    for (const type of types) {
      const c = comps.find(x => x.types.includes(type));
      if (c) return c.long_name;
    }
    return '';
  };
  const route = get('route');
  const streetNumber = get('street_number');
  return {
    localidad: get('locality', 'sublocality_level_1', 'administrative_area_level_2'),
    provincia: get('administrative_area_level_1'),
    direccion: route ? `${route}${streetNumber ? ` ${streetNumber}` : ''}` : '',
  };
}

function toPos(lat, lng) {
  const la = parseFloat(lat);
  const lo = parseFloat(lng);
  return (!isNaN(la) && !isNaN(lo)) ? { lat: la, lng: lo } : null;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function LocationPicker({ value, onChange, placeholder }) {
  // value: { address, lat, lng, direccion, localidad, provincia }
  // onChange: (data) => void  |  onChange(null) para limpiar

  const theme = useTheme();
  const acServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const debounceRef = useRef(null);
  const mapRef = useRef(null);
  const nativeMarkerRef = useRef(null);

  const defaultCenter = { lat: -34.6037, lng: -58.3816 };

  const initPos = toPos(value?.lat, value?.lng);

  const [center, setCenter] = useState(initPos || defaultCenter);
  const [markerPos, setMarkerPos] = useState(initPos);
  const [mapReady, setMapReady] = useState(false);
  const [searchVal, setSearchVal] = useState(value?.address || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) return;
    acServiceRef.current = new window.google.maps.places.AutocompleteService();
    const div = document.createElement('div');
    placesServiceRef.current = new window.google.maps.places.PlacesService(div);
    geocoderRef.current = new window.google.maps.Geocoder();
  }, [isLoaded]);

  // Sincronizar cuando value cambia desde el padre (ej. al reabrir con otro item via key={})
  useEffect(() => {
    if (!value) {
      setSearchVal('');
      setMarkerPos(null);
      setCenter(defaultCenter);
    } else {
      if (value.address !== undefined) setSearchVal(value.address || '');
      const pos = toPos(value.lat, value.lng);
      if (pos) {
        setCenter(pos);
        setMarkerPos(pos);
      } else {
        setMarkerPos(null);
      }
    }
  }, [value?.lat, value?.lng, value?.address]); // eslint-disable-line

  // ─── Marcador nativo (Marker JSX deprecado, no dibuja al montar con posición inicial) ──
  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;
    if (markerPos) {
      if (!nativeMarkerRef.current) {
        nativeMarkerRef.current = new window.google.maps.Marker({
          map: mapRef.current,
          position: markerPos,
          draggable: true,
        });
        nativeMarkerRef.current.addListener('dragend', (e) => {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          const pos = { lat, lng };
          setCenter(pos);
          setMarkerPos(pos);
          const baseData = {
            address: value?.address || '',
            direccion: value?.direccion || '',
            localidad: value?.localidad || '',
            provincia: value?.provincia || '',
            lat, lng,
          };
          onChange(baseData);
          if (geocoderRef.current) {
            geocoderRef.current.geocode({ location: pos }, (results, status) => {
              if (status === 'OK' && results[0]) {
                const addr = results[0].formatted_address || '';
                const comps = results[0].address_components || [];
                const { localidad, provincia, direccion } = parseAddressComponents(comps);
                if (addr) setSearchVal(addr);
                onChange({ address: addr, direccion, localidad, provincia, lat, lng });
              }
            });
          }
        });
      } else {
        nativeMarkerRef.current.setPosition(markerPos);
        nativeMarkerRef.current.setMap(mapRef.current);
      }
    } else {
      if (nativeMarkerRef.current) {
        nativeMarkerRef.current.setMap(null);
      }
    }
  }, [mapReady, markerPos, onChange, value]); // eslint-disable-line

  // Limpiar marcador al desmontar
  useEffect(() => {
    return () => {
      if (nativeMarkerRef.current) {
        nativeMarkerRef.current.setMap(null);
        nativeMarkerRef.current = null;
      }
    };
  }, []);

  // ─── Autocomplete ────────────────────────────────────────────────────────────

  const handleSearchChange = (e) => {
    const text = e.target.value;
    setSearchVal(text);
    clearTimeout(debounceRef.current);
    if (text.length < 2) { setSuggestions([]); setShowSugg(false); return; }
    debounceRef.current = setTimeout(() => {
      acServiceRef.current?.getPlacePredictions(
        { input: text, componentRestrictions: { country: 'ar' } },
        (predictions, status) => {
          if (status === 'OK' && predictions?.length) {
            setSuggestions(predictions);
            setShowSugg(true);
          } else {
            setSuggestions([]);
            setShowSugg(false);
          }
        }
      );
    }, 300);
  };

  const applyPlaceDetails = useCallback((place, formatted) => {
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const { localidad, provincia, direccion } = parseAddressComponents(place.address_components || []);
    const address = formatted || place.formatted_address || '';
    const pos = { lat, lng };
    setCenter(pos);
    setMarkerPos(pos);
    setSearchVal(address);
    onChange({ address, direccion, localidad, provincia, lat, lng });
  }, [onChange]);

  const handleSelectSuggestion = (prediction) => {
    setShowSugg(false);
    setSuggestions([]);
    placesServiceRef.current?.getDetails(
      { placeId: prediction.place_id, fields: ['geometry', 'address_components', 'formatted_address'] },
      (place, status) => {
        if (status !== 'OK' || !place?.geometry) return;
        applyPlaceDetails(place, prediction.description);
      }
    );
  };

  // ─── Click en el mapa ─────────────────────────────────────────────────────────

  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const pos = { lat, lng };
    setCenter(pos);
    setMarkerPos(pos);

    // Notificar inmediatamente para que el padre actualice value y el pin quede visible
    const baseData = {
      address: value?.address || searchVal || '',
      direccion: value?.direccion || '',
      localidad: value?.localidad || '',
      provincia: value?.provincia || '',
      lat, lng,
    };
    onChange(baseData);

    // Enriquecer con geocoding inverso si la API lo permite (best-effort)
    if (geocoderRef.current) {
      geocoderRef.current.geocode({ location: pos }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const place = results[0];
          const { localidad, provincia, direccion } = parseAddressComponents(place.address_components || []);
          const address = place.formatted_address || '';
          setSearchVal(address);
          onChange({ address, direccion, localidad, provincia, lat, lng });
        }
      });
    }
  }, [onChange, value, searchVal]);

  const handleClear = () => {
    setMarkerPos(null);
    setCenter(defaultCenter);
    setSearchVal('');
    setSuggestions([]);
    setShowSugg(false);
    clearTimeout(debounceRef.current);
    onChange(null);
  };

  const mapOptions = {
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControl: true,
    scrollwheel: true,
    gestureHandling: 'greedy',
    // El mapa metía OCHO paradas de Tab en medio del formulario (los controles
    // internos de Google: mover el mapa, atajos de teclado, "Terms"). Quien
    // carga con el teclado tenía que atravesarlas todas para llegar al campo
    // siguiente. La ubicación se elige con el buscador de direcciones de arriba
    // —ese sí es nuestro y sigue en el recorrido— o con el mouse sobre el mapa.
    keyboardShortcuts: false,
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (!KEY_VALID) {
    return (
      <NoKeyMsg>
        <i className="fas fa-map" style={{ marginRight: 6 }} />
        Selector de mapa no disponible — configurar <code>REACT_APP_GOOGLE_MAPS_API_KEY</code>
      </NoKeyMsg>
    );
  }

  if (loadError) {
    return <NoKeyMsg>Error al cargar Google Maps. Verificar la clave de API.</NoKeyMsg>;
  }

  if (!isLoaded) {
    return <div style={{ fontSize: '0.8rem', color: theme.colors.txtSecundario, padding: '8px 0' }}>Cargando mapa...</div>;
  }

  return (
    <SearchWrap>
      <InputWrap>
        <SearchRow>
          <SearchInp
            type="text"
            value={searchVal}
            onChange={handleSearchChange}
            onFocus={() => suggestions.length > 0 && setShowSugg(true)}
            onBlur={() => setTimeout(() => setShowSugg(false), 200)}
            placeholder={placeholder || 'Buscar dirección o lugar...'}
            autoComplete="off"
          />
          {markerPos && (
            <ClearBtn type="button" onClick={handleClear} title="Limpiar ubicación del mapa">
              × Limpiar
            </ClearBtn>
          )}
        </SearchRow>

        {showSugg && suggestions.length > 0 && (
          <SuggestionsList>
            {suggestions.map(s => (
              <SuggestionItem key={s.place_id} onMouseDown={() => handleSelectSuggestion(s)}>
                <i className="fas fa-map-marker-alt" style={{ opacity: 0.45, fontSize: 11, flexShrink: 0 }} />
                <SuggMain>{s.structured_formatting?.main_text || s.description}</SuggMain>
                <SuggSec>{s.structured_formatting?.secondary_text || ''}</SuggSec>
              </SuggestionItem>
            ))}
          </SuggestionsList>
        )}
      </InputWrap>

      {value?.address && (
        <SelectedAddr>
          <i className="fas fa-map-marker-alt" style={{ color: theme.colors.info }} />
          {value.address}
        </SelectedAddr>
      )}

      <MapHint>
        <i className="fas fa-mouse-pointer" style={{ marginRight: 4 }} />
        Clic en el mapa para colocar el punto · arrastrá el marcador para ajustar la posición
      </MapHint>

      {/* El div del mapa y sus controles internos quedan fuera del Tab (ver
          keyboardShortcuts arriba). Se sigue pudiendo arrastrar y hacer click. */}
      <GoogleMap
        mapContainerStyle={MAP_STYLE}
        center={center}
        zoom={markerPos ? 14 : 5}
        options={mapOptions}
        onClick={handleMapClick}
        onLoad={map => { mapRef.current = map; setMapReady(true); }}
        onUnmount={() => { mapRef.current = null; setMapReady(false); }}
      />
    </SearchWrap>
  );
}
